import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { ChevronRight, ShieldAlert, Waves, FileText } from "lucide-react";
import { CATEGORIES, CALCULATORS, getCategory, getCalculatorsByCategory, getWellControlCalculators } from "@/lib/catalog";
import { SafetyBanner } from "@/components/SafetyBanner";
import CategoryTile from "@/components/CategoryTile";
import { getSubseaProfile } from "@/lib/storage";
import { useStackMode } from "@/lib/stackModeContext";

export default function Categories() {
  const navigate = useNavigate();
  const { catId, stackMode: stackParam } = useParams();
  const { stackMode: globalStack, setStackMode: setGlobalStack } = useStackMode();
  const initialStack = stackParam === "subsea" || stackParam === "surface"
    ? stackParam
    : globalStack;
  const [stackMode, setStackMode] = useState(initialStack);
  const [profile, setProfile] = useState(() => getSubseaProfile());

  // Sync state when the URL param changes (e.g. navigating from Home → /categories/wellcontrol/subsea)
  useEffect(() => {
    const next = stackParam === "subsea" || stackParam === "surface" ? stackParam : globalStack;
    setStackMode(next);
    if (catId === "wellcontrol" && next !== globalStack) setGlobalStack(next);
    // Re-read the profile when returning from the profile page so the row reflects edits/deletes.
    setProfile(getSubseaProfile());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackParam]);

  // When the global toggle changes, mirror it into the local view (and URL).
  useEffect(() => {
    if (catId !== "wellcontrol") return;
    if (globalStack === stackMode) return;
    setStackMode(globalStack);
    navigate(`/categories/wellcontrol/${globalStack}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalStack]);

  const switchStack = (next) => {
    setStackMode(next);
    setGlobalStack(next);
    if (catId === "wellcontrol") {
      navigate(`/categories/wellcontrol/${next}`, { replace: true });
    }
  };

  if (catId) {
    const cat = getCategory(catId);
    if (!cat) {
      return (
        <div className="card-rs p-4 text-sm text-[var(--rs-text-2)]" data-testid="category-not-found">Category not found.</div>
      );
    }
    if (catId === "converters") { navigate("/converters", { replace: true }); return null; }
    if (catId === "library") { navigate("/library", { replace: true }); return null; }
    const isWellControl = catId === "wellcontrol";
    const list = isWellControl ? getWellControlCalculators(stackMode) : getCalculatorsByCategory(catId);
    const Icon = Icons[cat.icon] || Icons.Grid3x3;
    return (
      <div className="flex flex-col gap-5">
        <section>
          <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">Category</div>
          <div className="flex items-center gap-3 mt-1.5">
            <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "rgba(255,179,0,0.12)", border: "1px solid rgba(255,179,0,0.25)" }}>
              <Icon className="w-5 h-5" style={{ color: "var(--rs-amber)" }} />
            </div>
            <div>
              <h2 className="font-heading font-black text-2xl tracking-tight">{cat.name}</h2>
              <p className="text-xs text-[var(--rs-text-3)] mt-0.5">{cat.desc}</p>
            </div>
          </div>
        </section>

        {isWellControl && (
          <section data-testid="wc-stack-selector" className="flex flex-col gap-3">
            <div className="label-up text-[var(--rs-text-3)]">Well Control Mode</div>
            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="wc-tab-surface"
                onClick={() => switchStack("surface")}
                aria-pressed={stackMode === "surface"}
                className="card-rs p-4 text-left flex flex-col gap-2 transition-colors"
                style={{
                  borderColor: stackMode === "surface" ? "var(--rs-amber)" : "var(--rs-border)",
                  background: stackMode === "surface" ? "rgba(255,179,0,0.08)" : "transparent",
                }}
              >
                <ShieldAlert
                  className="w-5 h-5"
                  style={{ color: stackMode === "surface" ? "var(--rs-amber)" : "var(--rs-text-3)" }}
                />
                <div className="font-semibold text-sm">Surface Stack</div>
                <div className="text-[11px] text-[var(--rs-text-3)] leading-snug">
                  Land rig / platform stack at surface BOP.
                </div>
              </button>
              <button
                data-testid="wc-tab-subsea"
                onClick={() => switchStack("subsea")}
                aria-pressed={stackMode === "subsea"}
                className="card-rs p-4 text-left flex flex-col gap-2 transition-colors"
                style={{
                  borderColor: stackMode === "subsea" ? "#60A5FA" : "var(--rs-border)",
                  background: stackMode === "subsea" ? "rgba(33,150,243,0.10)" : "transparent",
                }}
              >
                <Waves
                  className="w-5 h-5"
                  style={{ color: stackMode === "subsea" ? "#60A5FA" : "var(--rs-text-3)" }}
                />
                <div className="font-semibold text-sm">Subsea Stack</div>
                <div className="text-[11px] text-[var(--rs-text-3)] leading-snug">
                  Floating rig with marine riser, BOP on the seabed.
                </div>
              </button>
            </div>
            {stackMode === "subsea" && <SafetyBanner subsea />}
          </section>
        )}

        {isWellControl && stackMode === "subsea" && (
          <section className="flex flex-col gap-3" data-testid="wc-subsea-section">
            <div>
              <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase" style={{ color: "#60A5FA" }}>Subsea Well Control</div>
              <h3 data-testid="wc-subsea-title" className="font-heading font-black text-xl tracking-tight mt-1">
                Subsea Stack Well Control
              </h3>
            </div>

            <div className="card-rs p-4 flex items-center justify-between gap-3" data-testid="wc-subsea-profile-row" style={{ borderColor: "rgba(33,150,243,0.4)" }}>
              <div className="min-w-0">
                <div className="font-semibold text-sm">{profile ? "Subsea Well Control Profile" : "No Subsea Well Control Profile yet"}</div>
                <div className="text-[11px] text-[var(--rs-text-3)] mt-0.5 leading-snug">
                  {profile
                    ? `${profile.rigName || "Rig"}  ·  BOP ${profile.bopType || "—"} @ ${profile.bopWorkingPressure || "—"} psi  ·  WD ${profile.waterDepth || "—"} ft  ·  Riser ${profile.riserLength || "—"} ft`
                    : "Capture rig-specific subsea stack data (BOP type, working pressure, water depth, riser, choke/kill geometry) for use in upcoming subsea calculators."}
                </div>
              </div>
              <button
                data-testid="wc-subsea-profile-btn"
                onClick={() => navigate("/subsea-profile")}
                className="h-10 px-3 rounded-md inline-flex items-center gap-2 text-xs label-up shrink-0"
                style={{ borderColor: "#60A5FA", color: "#60A5FA", border: "1px solid #60A5FA" }}
              >
                {profile ? "Open Subsea Well Control Profile" : "Add Subsea Well Control Profile"}
              </button>
            </div>

            <div className="card-rs p-4 text-sm text-[var(--rs-text-2)]" data-testid="wc-subsea-empty">
              <div className="font-semibold mb-1">Subsea Stack — section reserved</div>
              <div className="text-xs text-[var(--rs-text-3)] leading-relaxed">
                Subsea-specific calculators (marine riser hydrostatic, choke/kill line friction, BOP-on-bottom shut-in,
                trapped-pressure release, hydrate margins, etc.) will be added in upcoming batches. The mode selector,
                warning banners, save labelling, and per-well filtering are wired and ready.
              </div>
            </div>
          </section>
        )}

        {isWellControl && stackMode === "surface" && (
          <div className="card-rs p-3 text-xs text-[var(--rs-text-3)] flex items-start gap-2" data-testid="wc-surface-hint">
            <ShieldAlert className="w-4 h-4 shrink-0" style={{ color: "var(--rs-amber)" }} />
            <span>Showing <strong className="text-[var(--rs-amber)]">Surface Stack</strong> well-control calculators. Tap <strong>Subsea Stack</strong> above for subsea-specific tools and profile.</span>
          </div>
        )}

        {catId === "subsea-rig-formulas" && (
          <section className="flex flex-col gap-3" data-testid="subsea-rig-formulas-empty-state">
            <SafetyBanner subsea />
            <div className="card-rs p-3 text-xs leading-snug" style={{ borderColor: "#F57F17", background: "rgba(255,193,7,0.07)", color: "#FBD38D" }}>
              Subsea rig calculations require rig-specific equipment data, approved procedures, manufacturer information, verified dimensions, competent personnel, and engineering review where applicable. RigSentinel provides calculation support only and does not replace approved procedures, certified personnel, regulatory requirements, or engineering judgment.
            </div>
            <div className="card-rs p-4 text-sm text-[var(--rs-text-2)]" data-testid="subsea-rig-formulas-empty">
              <div className="font-semibold mb-1" style={{ color: "#60A5FA" }}>Subsea Rig Formulas</div>
              <div className="text-xs text-[var(--rs-text-3)] leading-relaxed">
                This section is prepared for offshore and subsea rig calculations including marine riser, choke line, kill line, riser margin, subsea stack records, pod/control records, and subsea field worksheets.
              </div>
            </div>
          </section>
        )}

        <div className="flex flex-col gap-2" data-testid={`category-${catId}-list`}>
          {list.length === 0 ? (
            (isWellControl && stackMode === "subsea") || catId === "subsea-rig-formulas" ? null : (
              <div className="card-rs p-4 text-sm text-[var(--rs-text-2)]">
                No calculators in this category yet. More tools coming.
              </div>
            )
          ) : list.map((c) => {
            const sm = c.stackMode || "surface";
            return (
              <button
                key={c.id}
                data-testid={`open-calc-${c.id}`}
                onClick={() => navigate(`/calc/${c.id}`)}
                className="card-rs p-3.5 flex items-center justify-between text-left hover:border-[var(--rs-amber)] transition-colors"
              >
                <div className="min-w-0 pr-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-sm">{c.name}</div>
                    {c.category === "wellcontrol" && (
                      <span
                        data-testid={`card-stack-${c.id}`}
                        className="label-up text-[9px] px-1.5 py-0.5 rounded shrink-0"
                        style={
                          sm === "subsea"
                            ? { background: "rgba(33,150,243,0.15)", color: "#60A5FA" }
                            : { background: "rgba(255,179,0,0.15)", color: "var(--rs-amber)" }
                        }
                      >
                        {sm === "subsea" ? "Subsea" : "Surface"}
                      </span>
                    )}
                    {c.comingSoon && (
                      <span data-testid={`badge-soon-${c.id}`} className="label-up text-[9px] px-1.5 py-0.5 rounded shrink-0" style={{ background: "rgba(255,179,0,0.15)", color: "var(--rs-amber)" }}>
                        Soon
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--rs-text-3)] mt-0.5 line-clamp-1">{c.description}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-[var(--rs-text-3)] shrink-0" />
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <section>
        <div className="font-mono-rs text-[10px] tracking-[0.22em] uppercase text-[var(--rs-amber)]">All Modules</div>
        <h2 className="font-heading font-black text-3xl tracking-tight mt-1">Categories</h2>
        <p className="text-sm text-[var(--rs-text-2)] mt-1.5">A complete oilfield calculation companion.</p>
      </section>
      <div className="grid grid-cols-2 gap-3" data-testid="all-categories-grid">
        {CATEGORIES
          .filter((c) => !c.stackMode || c.stackMode === globalStack)
          .map((c) => {
          const Icon = Icons[c.icon] || Icons.Grid3x3;
          const count = CALCULATORS.filter((x) => x.category === c.id).length;
          return (
            <CategoryTile
              key={c.id}
              dataTestId={`cat-${c.id}`}
              category={c}
              count={c.id === "library" ? undefined : count}
              Icon={Icon}
              onClick={() =>
                navigate(c.id === "converters" ? "/converters" : c.id === "library" ? "/library" : `/categories/${c.id}`)
              }
            />
          );
        })}
      </div>

      {/* Cameron Flange Slide Rule entry tile (reference tool, not a calculator category) */}
      <button
        type="button"
        data-testid="flange-slide-rule-tile"
        onClick={() => navigate("/tools/flange-slide-rule")}
        className="card-rs p-3.5 flex items-center gap-3 text-left transition-colors hover:border-[var(--rs-amber)]"
      >
        <div
          className="w-10 h-10 rounded-md inline-flex items-center justify-center shrink-0"
          style={{ background: "rgba(255,179,0,0.10)", color: "var(--rs-amber)" }}
        >
          <Icons.Ruler className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[13.5px]">Cameron Flange Slide Rule</div>
          <div className="text-[11.5px] text-[var(--rs-text-3)] truncate">Smart API Flange Finder — search, diagram, full specs, PDF.</div>
        </div>
        <ChevronRight className="w-4 h-4 text-[var(--rs-text-3)] shrink-0" />
      </button>

      {/* Decimal/Fractions Converter entry tile (reference tool, not a calculator category) */}
      <button
        type="button"
        data-testid="fraction-converter-tile"
        onClick={() => navigate("/tools/fraction-converter")}
        className="card-rs p-3.5 flex items-center gap-3 text-left transition-colors hover:border-[var(--rs-amber)]"
      >
        <div
          className="w-10 h-10 rounded-md inline-flex items-center justify-center shrink-0"
          style={{ background: "rgba(255,179,0,0.10)", color: "var(--rs-amber)" }}
        >
          <Icons.SquareDivide className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-[13.5px]">Decimal/Fractions Converter</div>
          <div className="text-[11.5px] text-[var(--rs-text-3)] truncate">Live 1/64&quot; conversions — 11/32&quot; ↔ 0.34375.</div>
        </div>
        <ChevronRight className="w-4 h-4 text-[var(--rs-text-3)] shrink-0" />
      </button>

      {/* Reports & Field Documentation entry tile (not a calculator category) */}
      <button
        type="button"
        data-testid="reports-tile"
        onClick={() => navigate("/reports")}
        className="card-rs p-3.5 flex items-center gap-3 text-left transition-colors hover:border-[var(--rs-amber)]"
      >
        <div
          className="w-10 h-10 rounded-md inline-flex items-center justify-center shrink-0"
          style={{
            background: "rgba(255,179,0,0.10)",
            border: "1px solid rgba(255,179,0,0.35)",
          }}
        >
          <FileText className="w-5 h-5" style={{ color: "var(--rs-amber)" }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading font-black text-base tracking-tight">Reports &amp; Field Documentation</div>
          <p className="text-[11px] text-[var(--rs-text-3)] mt-0.5 line-clamp-1">Daily Drilling Report and other rig-floor documentation tools.</p>
        </div>
        <ChevronRight className="w-4 h-4 text-[var(--rs-text-3)] shrink-0" />
      </button>
    </div>
  );
}
